import ResourceTyped from 'Resources/ResourceTyped';

type Conversation = {
	id: number;
	title: string;
	starred: boolean;
	contextType: string;
	contextId?: number;
	parentConversationId?: number | null;
	regDate: string;
	modDate: string;
	messages?: AssistantMessage[];
};

type AssistantMessage = {
	id: number;
	role: 'user' | 'assistant' | 'tool';
	content: string;
	metadata?: Record<string, unknown>;
	regDate: string;
};

type Memory = {
	id: number;
	type: string;
	key: string;
	value: string;
	source: string;
	regDate: string;
	modDate: string;
};

export type RoutineScope = 'user' | 'org';

export type HookFilter = {
	field: string;
	operator: string;
	value?: string | number | boolean | null;
};

export type Routine = {
	id: number;
	userId: number;
	scope: RoutineScope;
	agentId?: number | null;
	label?: string;
	prompt?: string;
	/** When set, the routine fires the workflow runner instead of the agent prompt path. */
	workflowId?: number | null;
	/** Optional trigger-payload override for workflow runs (cron-scoped only; event-scoped routines get the entity payload). */
	payload?: Record<string, unknown> | null;
	enabled: boolean;
	conversationId?: number;
	cronExpression?: string | null;
	lastRun?: string;
	/** Derived: the pinned conversation has ≥1 succeeded run (incl. a test-run),
	 *  so there's something for the compiler to build a Playbook from. */
	hasCompilableRun?: boolean;
	nextRun?: string;
	entityType?: string | null;
	event?: 'insert' | 'update' | null;
	filters?: HookFilter[];
	cooldownSeconds?: number;
	lastFired?: string;
	/** Which trigger kind fires the routine. Derived: webhookHash ⇒ 'webhook', entityType ⇒ 'event', cronExpression ⇒ 'cron'. */
	trigger?: 'cron' | 'event' | 'webhook';
	/** Set for webhook-scoped routines (WEB-7446). The unguessable `wh_` hash that forms the public endpoint URL. */
	webhookHash?: string | null;
	/** Webhook tuning — currently just the per-routine rate-limit override (req/min, capped at 1000). */
	webhookConfig?: { rateLimit?: number } | null;
	regDate?: string;
	modDate?: string;
};

/** A single inbound webhook delivery (one dispatched run), for the detail panel (WEB-7446). */
export type WebhookDelivery = {
	id: number;
	status: string;
	error?: string | null;
	startedAt?: string | null;
	finishedAt?: string | null;
};

/**
 * Mirror of the backend `CompiledWorkflowDraft` shape (see
 * `base/src/core/services/ai/agent/workflow/compiler/llmCompiler.types.ts`)
 * with only the fields the read-only review modal needs. Backend types use
 * `unknown` for `args` and similar — we keep that loose here so the modal
 * can render whatever the compiler produced without lying about shape.
 */
export type CompiledPlaybookStep =
	| { kind: 'tool'; id: string; tool: string; args: unknown; bind?: string; onError?: string }
	| { kind: 'llm'; id: string; prompt: unknown; output: unknown; bind: string; onError?: string }
	| { kind: 'if'; id: string; cond: string; then: string[]; else?: string[] }
	| { kind: 'foreach'; id: string; over: string; as: string; body: string[]; bind?: string };

export type CompiledPlaybookArgDecision =
	| { kind: 'keep-constant'; rationale: string }
	| { kind: 'keep-binding'; rationale: string }
	| { kind: 'promote-template'; template: string; rationale: string }
	| { kind: 'promote-llm-step'; promotedStepId: string; rationale: string };

export type CompiledPlaybookWarning = {
	code: string;
	stepId?: string;
	argPath?: string;
	path?: string;
	message: string;
};

/**
 * WEB-6799 — inbox items. Two kinds today: `approval` (a pending approval
 * request targeted at the user) and `unread` (a conversation with unseen agent
 * activity). Future iterations may add delegations as new
 * kinds. Consumers must switch on `kind` rather than assume approval-shape
 * fields exist.
 */
export type InboxApprovalItem = {
	id: number;
	kind: 'approval';
	action: string;
	conversationId: number;
	/** Title of the conversation this proposal belongs to, for the Inbox page. */
	title: string | null;
	runId: number;
	agentId: number;
	regDate: string;
	expiresAt?: string;
};

export type InboxConversationItem = {
	id: number;
	/** `unread` = unseen agent activity; `earlier` = already seen (shown dimmed). */
	kind: 'unread' | 'earlier';
	conversationId: number;
	title: string | null;
	/** Conversation context type — drives the row's tag (Routine vs Message). */
	contextType: string | null;
	/** The latest agent message, used for the one-line preview. */
	lastMessage: string | null;
	regDate: string;
};

/**
 * A Playbook whose latest run failed (WEB-7821) — an ambient "needs attention"
 * signal, not a chat message. Clears automatically once the next run succeeds.
 */
export type InboxFailedRunItem = {
	id: string;
	kind: 'failedRun';
	runId: number;
	workflowId: number;
	conversationId: number | null;
	/** The bound routine's label, or null (UI falls back to a generic title). */
	title: string | null;
	error: string | null;
	regDate: string | null;
};

export type InboxItem = InboxApprovalItem | InboxConversationItem | InboxFailedRunItem;

export type CompiledPlaybookDraft = {
	sourceRunId: number;
	workflow: {
		trigger: { kind: string; [k: string]: unknown };
		steps: CompiledPlaybookStep[];
	};
	provenance: Record<
		string,
		{
			sourceEventSeq: number;
			args: Record<
				string,
				{ kind: 'constant' | 'binding-step' | 'binding-trigger' | 'generated'; [k: string]: unknown }
			>;
		}
	>;
	warnings: CompiledPlaybookWarning[];
	validationIssues: Array<{ message: string; path?: string }>;
	decisions: Record<string, CompiledPlaybookArgDecision>;
	compilerWarnings: CompiledPlaybookWarning[];
	compilerVersion: { model: string; promptHash: string };
	/** Per-step curated name + description from the tool registry (presentation only). */
	stepMeta?: Record<string, { name: string; description: string }>;
	/** Per-step LLM-generated action title + context-rich description (presentation only). */
	stepSummaries?: Record<string, { title: string; description: string }>;
	/**
	 * Cross-run consensus summary (Phase 2). Present when compiled from a routine.
	 * `confident: false` means the routine's recent runs disagreed on structure —
	 * a signal it may work better as an agent routine.
	 */
	runConsistency?: {
		runsAnalyzed: number;
		cohortSize: number;
		signature: string[];
		confident: boolean;
	};
	/**
	 * Average per-run cost of the routine as an agent (`before`) vs as a compiled
	 * Playbook (`after`), for the credit-saving comparison (WEB-7810). `after` is
	 * null until the routine has run as a Playbook at least once.
	 */
	costComparison?: {
		before: { avgCredits: number; avgCostCents: number; runs: number } | null;
		after: { avgCredits: number; avgCostCents: number; runs: number } | null;
		/** True when `after` is projected from the draft's LLM steps rather than
		 *  measured from real Playbook runs (WEB-7816). */
		afterEstimated?: boolean;
	};
};

/**
 * Forced representation for a single argument when the user flips its toggle in
 * the editor (WEB-6632). `'template'` = static value, no per-run AI;
 * `'llm-step'` = regenerate per run via Claude.
 */
export type PlaybookArgMode = 'template' | 'llm-step';

/** Result of re-deriving a single argument — the full updated workflow + the new decision. */
export type RecompilePlaybookArgResult = {
	workflow: CompiledPlaybookDraft['workflow'];
	decision: CompiledPlaybookArgDecision;
	warnings: CompiledPlaybookWarning[];
};

/** Result of a manual workflow dispatch (`POST /agentWorkflows/:id/run`). */
export type PlaybookRunResult = { runId: number | null; status: string; error?: string };

type CustomerKnowledge = {
	id: number;
	category: string;
	key: string;
	value: string;
	confidence: number;
	usageCount: number;
	lastValidated?: string;
};

export type AdminUsage = {
	period: { label: string; from: string; to: string };
	orgBudget: {
		spentCents: number;
		spentCreditsSek: number;
		limitSek: number;
		dailySeries: number[];
		dailySeriesCreditsSek: number[];
	};
	users: Array<{
		userId: number;
		name: string;
		role: string | null;
		personalSpendCents: number;
		personalSpendCreditsSek: number;
		orgSpendCents: number;
		orgSpendCreditsSek: number;
		agentCount: number;
		canCreateAgents: boolean;
		canAccessWorkspace: boolean;
		isAdmin: boolean;
		/** WEB-6650 — explicit per-user override; null = inherit default. */
		aiCreditCapOverrideSek: number | null;
		/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		ownerUserId: number | null;
		ownerName: string | null;
		avatarUrl: string | null;
		tasks: number;
		successPct: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	promotionPending: number;
	kpis: {
		totalSpendCents: number;
		totalSpendCreditsSek: number;
		tasks: number;
		avgSuccessPct: number;
		topSpenderUserId: number | null;
		mostUsedAgentId: number | null;
	};
};

export type CreditsSnapshot = {
	period: { label: string; from: string; to: string };
	allocatedSek: number;
	consumedSek: number;
	remainingSek: number;
	overageSek: number;
	activeUserCount: number;
	effectiveUsdToSekRate: number;
	marginMultiplier: number;
	/** Current-month live meter. ONLY populated when the snapshot's
	 *  period covers "now" (current month, last30d, all) — null for past
	 *  periods, so don't wire banners to a past-period snapshot and expect
	 *  live state. Also null when the meter is unreadable (fail open). */
	liveConsumedSek: number | null;
	/** Live meter ≥ the org-wide monthly cap right now. Always false
	 *  for past periods or an unreadable meter — see liveConsumedSek. */
	creditsExhausted: boolean;
	perUser: Array<{
		userId: number;
		name: string | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
		/** WEB-6650 — resolved per-user credit cap (SEK). 0 = unlimited. */
		effectivePersonalCreditCapSek: number;
	}>;
	perAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		costUsd: number;
		creditsSek: number;
		callCount: number;
	}>;
	perTool: Array<{ feature: string; costUsd: number; creditsSek: number; callCount: number }>;
	perDay: Array<{ day: string; costUsd: number; creditsSek: number; callCount: number }>;
	customerPaidConnections: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
	customerPaidPerUser: Array<{ userId: number; name: string | null; callCount: number }>;
	customerPaidPerAgent: Array<{
		agentId: number;
		name: string | null;
		visibility: 'org' | 'personal' | null;
		callCount: number;
	}>;
	customerPaidPerDay: Array<{ day: string; callCount: number }>;
	// WEB-6651: notetaker (and any future tool) usage covered by a legacy
	// addon. Same shape as customerPaidConnections; differentiated server-side
	// by `metadata.coverage === 'addon'` on the ai_tracking row. Never folded
	// into credits — the addon already pays for it.
	addonCoveredUsage: Array<{
		feature: string;
		type: string | null;
		callCount: number;
		totalDurationMs: number | null;
	}>;
};

export type PriceBookItem = {
	toolKey: string;
	vendor: string;
	category: 'llm' | 'web' | 'notetaker' | 'other';
	unit: string;
	pricePerUnitSek: number;
};

// WEB-7168 — Forecast admin types. Hand-maintained mirrors of the response
// shapes in `api/src/controller/customer/ai/forecastAdmin.ts` (the UI can't
// import API types) — update both when the shape changes.
export type ForecastSettings = {
	continuousRefresh: boolean;
};

export type ForecastCostEstimate = {
	deal: {
		candidateDeals: number;
		estimatedRelevantDeals: number;
		willTranslate: boolean;
		monthlyCredits: number | null;
	};
	pipeline: {
		wouldCallLLM: boolean;
		reasonSkipped: string | null;
		monthlyCredits: number | null;
	};
	totalMonthlyCredits: number | null;
	estimatedAt: string;
};

export type PromotionRequest = {
	id: number;
	agentId: number;
	requestedByUserId: number;
	pitchText: string | null;
	status: 'pending' | 'approved' | 'declined';
	decidedByUserId?: number | null;
	decidedAt?: string | null;
	regDate: string;
	modDate: string;
};

export type UserUsage = {
	personalSpendCents: number;
	personalSpendCreditsSek: number;
	personalCapSek: number;
	/** WEB-6650 — resolved cap (override → org default). 0 = unlimited. */
	effectivePersonalCreditCapSek: number;
	agents: Array<{
		agentId: number;
		name: string;
		visibility: 'org' | 'personal';
		status: string;
		avatarUrl: string | null;
		budgetMonthlySek: number;
		spentMonthlyCents: number;
		spentMonthlyCreditsSek: number;
		tasksCompleted14d: number;
		tasksFailed14d: number;
		pausedReason: string | null;
	}>;
	totalSpendCents: number;
	totalSpendCreditsSek: number;
	tasksCompleted14d: number;
	tasksFailed14d: number;
	successRate14d: number;
	histogram: Array<{
		day: string;
		completed: number;
		failed: number;
		spendCents: number;
		spendCreditsSek: number;
	}>;
	pool: {
		period: { label: string; from: string; to: string };
		allocatedSek: number;
		consumedSek: number;
		remainingSek: number;
		overageSek: number;
		userCreditsSek: number;
		userSharePct: number;
		/** Current-month live meter (null when unreadable — fail open). */
		liveConsumedSek: number | null;
		/** Live meter ≥ the org-wide monthly cap right now. */
		creditsExhausted: boolean;
	} | null;
};

export type AiAgentPolicies = {
	/**
	 * Org-wide default per-user monthly cap on personal AI agent spend (SEK).
	 * Also the WEB-6650 fallback when a user has no `ai_credit_cap_sek`
	 * override. 0 = unlimited.
	 */
	personalCapSek: number;
	orgMonthlyBudgetSek: number;
	promotionMinTasks: number;
	promotionMinSuccessPct: number;
	minExecutionIntervalMinutes: number;
	permissionUpgradeMinApprovals: number;
};

/**
 * Admin policies response. Mirrors {@link AiAgentPolicies} plus the org-budget
 * ceiling: a hard cap the admin cannot raise the org budget above (flag-only /
 * unpaid trial orgs), or `null` when the account has no such ceiling. When set,
 * the limits UI locks the org-budget field and explains why a higher value
 * won't stick. Kept off {@link AiAgentPolicies} so the editable-field keys stay
 * purely numeric.
 */
export type AiAgentPoliciesResponse = AiAgentPolicies & { orgBudgetCeilingSek: number | null };

export type CompanyProfile = {
	business: string;
	icp: string;
	competitors: string;
};

export type CompanyProfilePayload = CompanyProfile & { maxFieldLength: number };

export type PageCompanyMatch =
	| { status: 'none' }
	| { status: 'unknown' }
	| { status: 'existing'; clientId: number; clientName: string }
	| { status: 'new'; companyName: string; prospectingId: string; orgNumber: string | null };

export type PageCompanyContact = {
	name: string;
	role?: string;
	// The person's own employer, when the page states it. Drives the "Other people
	// found" grouping and which company they're added to.
	company?: string;
	email?: string;
	phone?: string;
	linkedin?: string;
	existsInCrm?: boolean;
	// Server-tagged: does this person work at the page's company?
	belongsToPage?: boolean;
};

export type PageCompanyOverrides = {
	name?: string;
	orgNumber?: string;
	phone?: string;
	street?: string;
	zip?: string;
	city?: string;
};

export type PageCompanyExtract = {
	company: {
		name: string;
		domain: string | null;
		orgNumber: string | null;
		phone: string | null;
		street: string | null;
		zip: string | null;
		city: string | null;
	} | null;
	contacts: PageCompanyContact[];
	prospecting: {
		prospectingId: string;
		name: string;
		domain?: string;
		city?: string;
		orgNumber?: string;
	} | null;
	crm: { exists: boolean; clientId?: number; clientName?: string } | null;
};

export type PagePersonCompany = {
	name: string;
	prospectingId?: string;
	domain?: string;
	orgNumber?: string | null;
	crm: { exists: boolean; clientId?: number; clientName?: string };
};

export type PagePersonExtract = {
	person: {
		name: string;
		title?: string;
		companyName?: string;
		linkedin?: string;
		existsInCrm?: boolean;
		contactId?: number;
	} | null;
	company: PagePersonCompany | null;
};

export type PagePersonCompanyOption = {
	clientId: number;
	name: string;
	orgNumber: string | null;
	domain: string | null;
};

export type PageCompanyAddResult = {
	clientId: number;
	clientName: string;
	companyCreated: boolean;
	contactsCreated: number;
	contactsSkipped: number;
};

// Error message from the v2 `{error, data}` envelope, status-text fallback.
const extractApiError = (body: unknown, status: number, fallback: string): Error => {
	const env = body as { error?: { message?: unknown } | string };
	const inner = typeof env?.error === 'string' ? env.error : env?.error?.message;
	const message = typeof inner === 'string' && inner ? inner : `${fallback} (${status})`;
	return new Error(message);
};

class Assistant extends ResourceTyped<Conversation> {
	constructor() {
		super('assistant/conversations');
	}

	createConversation(contextType = 'general', contextId?: number): Promise<{ id: number }> {
		return this._postRequest('', { contextType, contextId }).then(res => res.data?.data ?? res.data);
	}

	getConversation(id: number): Promise<Conversation> {
		return this._getRequest(String(id)).then(res => res.data?.data ?? res.data);
	}

	// WEB-7348: resolve the shared authoring conversation for a page. Visibility
	// follows page access, so every admin opening the page gets the same thread
	// (not just its creator). Resolves to null when the page has no conversation
	// yet — none is created until the first message is sent.
	getPageConversation(pageId: number): Promise<Conversation | null> {
		return fetch(`/api/v2/assistant/pages/${pageId}/conversation`, {
			headers: { 'Content-Type': 'application/json' }
		})
			.then(res => res.json())
			.then(res => res?.data ?? null);
	}

	deleteConversation(id: number): Promise<void> {
		return this._deleteRequest(String(id)).then(() => undefined);
	}

	renameConversation(id: number, title: string): Promise<void> {
		return this._putRequest(String(id), { title }).then(() => undefined);
	}

	starConversation(id: number, starred: boolean): Promise<void> {
		return fetch(`/api/v2/assistant/conversations/${id}/star`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ starred })
		}).then(() => undefined);
	}

	updatePostMeetingMessage(
		conversationId: number,
		messageId: number,
		updates: { content?: string; title?: string }
	): Promise<void> {
		return this._putRequest(`${conversationId}/post-meeting-message/${messageId}`, updates).then(() => undefined);
	}

	listConversations(): Promise<Conversation[]> {
		return this._getRequest('').then(res => res.data?.data ?? res.data);
	}

	/**
	 * Headless send — dispatch the agent run to the worker instead of streaming
	 * inline over SSE. Returns immediately; the run's output arrives over Pusher
	 * (`agent.runStarted` / `runEvent` / `runFinalized`), watched via
	 * `useAgentRunEvents`. Used by page authoring, where generation can outrun
	 * the HTTP request timeout.
	 */
	async sendMessageHeadless(
		conversationId: number,
		message: string,
		files?: File[],
		regenerate?: boolean
	): Promise<{ conversationId: number }> {
		const url = `/api/v2/assistant/conversations/${conversationId}/messages/async`;
		let body: BodyInit;
		const headers: Record<string, string> = {};
		// Mirror the SSE send: multipart when files are attached (the API persists
		// them as context_items for the worker to rebuild), JSON otherwise.
		// Regenerate never carries files, so it always takes the JSON branch — the
		// flag re-runs the existing turn instead of posting a new user message
		// (WEB-7801).
		if (files && files.length > 0) {
			const formData = new FormData();
			formData.append('message', message);
			for (const file of files) {
				formData.append('files', file);
			}
			body = formData;
		} else {
			headers['Content-Type'] = 'application/json';
			body = JSON.stringify({ message, ...(regenerate ? { regenerate: true } : {}) });
		}

		const response = await fetch(url, { method: 'POST', headers, body });
		if (!response.ok) {
			throw new Error('Failed to send message');
		}
		const json = await response.json();
		return json.data ?? json;
	}

	async sendMessageStream(
		conversationId: number,
		message: string,
		contextType: string = 'general',
		contextId?: number,
		options: {
			files?: File[];
			isScheduledRun?: boolean;
			runId?: string;
			runTimestamp?: string;
			regenerate?: boolean;
			turnContext?: string;
			trustedContext?: string;
			signal?: AbortSignal;
			onText?: (text: string) => void;
			onDone?: () => void;
			onError?: (error: string) => void;
			onPanel?: (panel: unknown) => void;
			onImages?: (urls: string[]) => void;
			onAttachments?: (
				attachments: Array<{ name: string; mimeType: string; sizeBytes?: number; url?: string }>
			) => void;
			onDocument?: (attachment: { name: string; mimeType: string; sizeBytes?: number; url?: string }) => void;
			onStatus?: (status: string) => void;
			onMultiAction?: (config: unknown) => void;
			onWriteProposal?: (proposal: {
				entity: string;
				action: string;
				data: Record<string, unknown>;
				approvalId?: number | null;
				matchingContacts?: number;
			}) => void;
			onEmailDraft?: (draft: {
				contactIds: number[];
				subject: string;
				body: string;
				ccContactIds?: number[];
				clientId?: number;
				attachments?: { id: number; name: string }[];
				approvalId?: number | null;
			}) => void;
			onOfferDraft?: (draft: {
				clientId: number;
				contactId: number;
				products: {
					productId: number;
					productName?: string;
					quantity: number;
					unitPrice?: number;
					discount?: number;
				}[];
				currency: string;
				validUntil: string;
				description?: string;
				notes?: string;
				approvalId?: number | null;
			}) => void;
			onAgentCreated?: (info: { agentId: number; name: string; updated?: boolean }) => void;
			onIdentityUpdated?: (identity: { name?: string; role?: string; avatarUrl?: string | null }) => void;
			onThinking?: (snapshot: string) => void;
			onSuggestedReplies?: (replies: string[]) => void;
			onDelegation?: (delegation: {
				conversationId: number;
				agentId: number;
				agentName?: string;
				title?: string;
				rationale?: string;
				status: string;
			}) => void;
		} = {}
	): Promise<void> {
		const {
			files,
			isScheduledRun,
			runId,
			runTimestamp,
			regenerate,
			turnContext,
			trustedContext,
			signal,
			onText,
			onDone,
			onError,
			onPanel,
			onImages,
			onAttachments,
			onDocument,
			onStatus,
			onMultiAction,
			onWriteProposal,
			onEmailDraft,
			onOfferDraft,
			onAgentCreated,
			onIdentityUpdated,
			onThinking,
			onSuggestedReplies,
			onDelegation
		} = options;
		let body: BodyInit;
		const headers: Record<string, string> = {};

		if (files && files.length > 0) {
			const formData = new FormData();
			formData.append('message', message);
			formData.append('contextType', contextType);
			if (contextId !== undefined) formData.append('contextId', String(contextId));
			if (turnContext) formData.append('turnContext', turnContext);
			if (trustedContext) formData.append('trustedContext', trustedContext);
			for (const file of files) {
				formData.append('files', file);
			}
			body = formData;
		} else {
			headers['Content-Type'] = 'application/json';
			body = JSON.stringify({
				message,
				contextType,
				contextId,
				...(isScheduledRun && { isScheduledRun: true }),
				...(runId && { runId }),
				...(runTimestamp && { runTimestamp }),
				...(regenerate && { regenerate: true }),
				...(turnContext && { turnContext }),
				...(trustedContext && { trustedContext })
			});
		}

		let response: Response;
		try {
			response = await fetch(`/api/v2/assistant/conversations/${conversationId}/messages`, {
				method: 'POST',
				headers,
				body,
				signal
			});
		} catch (err) {
			// Silent exit on user-triggered abort — the caller is responsible for
			// cleaning up UI state. Anything else surfaces as an error.
			if (err instanceof DOMException && err.name === 'AbortError') return;
			onError?.('Failed to send message');
			return;
		}

		if (!response.ok || !response.body) {
			onError?.('Failed to send message');
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		let gotDone = false;
		let gotError = false;
		let streamDone = false;

		while (!streamDone) {
			let readResult: ReadableStreamReadResult<Uint8Array>;
			try {
				readResult = await reader.read();
			} catch (err) {
				// Abort during read — stop cleanly without firing onError.
				if (err instanceof DOMException && err.name === 'AbortError') return;
				throw err;
			}
			const { done, value } = readResult;
			if (done) {
				streamDone = true;
				break;
			}

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';

			for (const line of lines) {
				if (line.startsWith('data: ')) {
					try {
						const data = JSON.parse(line.slice(6));
						if (data.type === 'text' && data.text) {
							onText?.(data.text);
						} else if (data.type === 'panel' && data.panel) {
							onPanel?.(data.panel);
						} else if (data.type === 'images' && data.urls) {
							onImages?.(data.urls);
						} else if (data.type === 'attachments' && Array.isArray(data.attachments)) {
							onAttachments?.(data.attachments);
						} else if (data.type === 'document' && data.attachment) {
							onDocument?.(data.attachment);
						} else if (data.type === 'status' && data.text) {
							onStatus?.(data.text);
						} else if (data.type === 'multi_action' && data.config) {
							onMultiAction?.(data.config);
						} else if (data.type === 'write_proposal' && data.proposal) {
							onWriteProposal?.(data.proposal);
						} else if (data.type === 'email_draft' && data.emailDraft) {
							onEmailDraft?.(data.emailDraft);
						} else if (data.type === 'offer_draft' && data.offerDraft) {
							onOfferDraft?.(data.offerDraft);
						} else if (data.type === 'agent_created' && data.agentCreated) {
							onAgentCreated?.(data.agentCreated);
						} else if (data.type === 'identity_updated' && data.identity) {
							onIdentityUpdated?.(data.identity);
						} else if (data.type === 'thinking' && data.text) {
							onThinking?.(data.text);
						} else if (data.type === 'suggested_replies' && Array.isArray(data.suggestedReplies)) {
							onSuggestedReplies?.(
								data.suggestedReplies.filter((r: unknown): r is string => typeof r === 'string')
							);
						} else if (data.type === 'delegation' && data.delegation) {
							onDelegation?.(data.delegation);
						} else if (data.type === 'done') {
							gotDone = true;
							onDone?.();
						} else if (data.type === 'error') {
							gotError = true;
							onError?.(data.error);
						}
					} catch {
						// Skip malformed events
					}
				}
			}
		}

		// Process any remaining data in the buffer
		if (buffer.trim().startsWith('data: ')) {
			try {
				const data = JSON.parse(buffer.trim().slice(6));
				if (data.type === 'text' && data.text) {
					onText?.(data.text);
				} else if (data.type === 'done') {
					gotDone = true;
					onDone?.();
				}
			} catch {
				// Skip malformed
			}
		}

		// If stream ended without a done event, call onDone anyway (unless error already handled it)
		if (!gotDone && !gotError) {
			onDone?.();
		}
	}

	getMemories(): Promise<Memory[]> {
		return fetch('/api/v2/assistant/memory')
			.then(res => res.json())
			.then(res => res.data);
	}

	updateMemory(memoryId: number, value: string): Promise<void> {
		return fetch(`/api/v2/assistant/memory/${memoryId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ value })
		}).then(() => undefined);
	}

	deleteMemory(memoryId: number): Promise<void> {
		return fetch(`/api/v2/assistant/memory/${memoryId}`, { method: 'DELETE' }).then(() => undefined);
	}

	getCustomerKnowledge(): Promise<CustomerKnowledge[]> {
		return fetch('/api/v2/assistant/customer-knowledge')
			.then(res => res.json())
			.then(res => res.data);
	}

	createCustomerKnowledge(entry: {
		category: string;
		key: string;
		value: string;
		confidence?: number;
	}): Promise<CustomerKnowledge> {
		return fetch('/api/v2/assistant/customer-knowledge', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(entry)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	updateCustomerKnowledge(
		id: number,
		updates: { key?: string; value?: string; category?: string; confidence?: number }
	): Promise<void> {
		return fetch(`/api/v2/assistant/customer-knowledge/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(() => undefined);
	}

	deleteCustomerKnowledge(id: number): Promise<void> {
		return fetch(`/api/v2/assistant/customer-knowledge/${id}`, { method: 'DELETE' }).then(() => undefined);
	}

	getRoutines(): Promise<Routine[]> {
		return fetch('/api/v2/assistant/routines')
			.then(res => res.json())
			.then(res => res.data);
	}

	/**
	 * WEB-6799 — fetch the user's Inbox. v1 = pending approval requests
	 * targeted at the caller, oldest first. Backend filters; client just
	 * renders.
	 */
	getInbox(): Promise<InboxItem[]> {
		return fetch('/api/v2/assistant/inbox')
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	/** WEB-7943 — clear a single conversation from the Inbox unread feed. */
	async markInboxRead(conversationId: number): Promise<void> {
		const res = await fetch(`/api/v2/assistant/inbox/${conversationId}/read`, { method: 'PUT' });
		if (!res.ok) throw new Error(`Failed to mark conversation as read (${res.status})`);
	}

	/** WEB-7943 — clear the given unread conversations (the rows currently shown). */
	async markAllInboxRead(conversationIds: number[]): Promise<void> {
		const res = await fetch('/api/v2/assistant/inbox/read-all', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ conversationIds })
		});
		if (!res.ok) throw new Error(`Failed to mark inbox as read (${res.status})`);
	}

	/**
	 * Compile a Playbook draft from the routine's most recent succeeded run.
	 * Returns the raw `CompiledPlaybookDraft` for read-only review (no DB write
	 * — the visual editor is what persists). Throws with a user-readable
	 * message when the routine has no succeeded run yet.
	 */
	async compilePlaybookFromRoutine(routineId: number): Promise<CompiledPlaybookDraft> {
		const res = await fetch('/api/v2/agentWorkflows/compile-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Compile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as CompiledPlaybookDraft;
	}

	/**
	 * Compile a Playbook draft from a specific `agent_run` (WEB-6857). Used by
	 * the reply-attached "Compile to Playbook" button — the run id is read
	 * from `assistant_message.metadata.agentRunId`. No DB write; returns the
	 * draft for the review modal.
	 */
	async compilePlaybookFromRun(runId: number): Promise<CompiledPlaybookDraft> {
		const res = await fetch('/api/v2/agentWorkflows/compile-from-run', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ runId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Compile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as CompiledPlaybookDraft;
	}

	/**
	 * Persist a compiled Playbook draft (WEB-6850 / WEB-6857).
	 *
	 * Two modes:
	 * - **Routine-bound** (`routineId: number`) — also binds the routine's
	 *   `workflowId` so its next trigger dispatches the workflow runner
	 *   instead of the agent prompt path.
	 * - **Standalone** (`routineId: null`) — just persists the `agent_workflow`
	 *   row; the user wires a trigger later. Permission falls back to source
	 *   run ownership on the backend.
	 */
	async savePlaybookFromCompiled(
		routineId: number | null,
		draft: CompiledPlaybookDraft,
		name?: string
	): Promise<{ workflowId: number }> {
		const trimmedName = name && name.trim().length > 0 ? name.trim() : undefined;
		const payload = {
			...(routineId == null ? {} : { routineId }),
			...(trimmedName ? { name: trimmedName } : {}),
			draft
		};
		const res = await fetch('/api/v2/agentWorkflows/save-from-compiled', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload)
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Save request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as { workflowId: number };
	}

	/**
	 * Unbind the routine's Playbook so it reverts to the agent-prompt path on
	 * the next trigger. The previous `agent_workflow` row stays in the DB as
	 * an audit trail — only `routine.workflowId` is cleared.
	 */
	async unsetPlaybookFromRoutine(routineId: number): Promise<void> {
		const res = await fetch('/api/v2/agentWorkflows/unset-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Unset request failed (${res.status})`;
			throw new Error(message);
		}
	}

	/**
	 * Recompile the routine's Playbook from its latest succeeded run. Creates
	 * a fresh `agent_workflow` row and rebinds the routine to it; the previous
	 * workflow row stays in the DB for audit. Returns the new workflow id and
	 * the just-saved draft so the UI can show it in a read-only review modal
	 * without a second round-trip.
	 */
	async recompilePlaybookFromRoutine(
		routineId: number
	): Promise<{ workflowId: number; draft: CompiledPlaybookDraft }> {
		const res = await fetch('/api/v2/agentWorkflows/recompile-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Recompile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as { workflowId: number; draft: CompiledPlaybookDraft };
	}

	/**
	 * Read-only view of a routine's saved Playbook — returns the stored
	 * CompiledPlaybookDraft with no recompile/LLM call/mutation (WEB-7810).
	 */
	async viewPlaybookFromRoutine(routineId: number): Promise<CompiledPlaybookDraft> {
		const res = await fetch('/api/v2/agentWorkflows/view-from-routine', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ routineId })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `View request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as CompiledPlaybookDraft;
	}

	/**
	 * Re-derive a single argument of an in-flight draft into a forced
	 * representation (WEB-6632). Powers the editor's per-field
	 * "fill-in-the-blank ↔ needs AI" toggle. No DB write — the caller merges the
	 * returned `workflow` + `decision` into its in-memory draft and saves later.
	 */
	async recompilePlaybookArg(args: {
		draft: CompiledPlaybookDraft;
		stepId: string;
		argPath: string;
		mode: PlaybookArgMode;
		agentPrompt?: string;
	}): Promise<RecompilePlaybookArgResult> {
		const res = await fetch('/api/v2/agentWorkflows/recompile-arg', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(args)
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Recompile request failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as RecompilePlaybookArgResult;
	}

	/**
	 * Manually dispatch a saved Playbook with an optional sample payload
	 * (WEB-6632 "Test run"). Returns `{ runId, status }` so the caller can open
	 * the trace drawer against the run.
	 */
	async runPlaybook(workflowId: number, payload?: Record<string, unknown>): Promise<PlaybookRunResult> {
		const res = await fetch(`/api/v2/agentWorkflows/${workflowId}/run`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload ? { payload } : {})
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Test run failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as PlaybookRunResult;
	}

	/**
	 * Target-aware test run for a routine (WEB-7734). For a deterministic
	 * routine (bound to a `workflowId`) the backend dispatches the compiled
	 * workflow through the same runner its trigger uses and lands the run on
	 * the routine's conversation. Returns `{ runId, status, conversationId }`.
	 * Prompt routines are test-run from chat client-side, not through here.
	 */
	async testRunRoutine(
		routineId: number
	): Promise<{ runId: number | null; status: string; errorMessage: string | null; conversationId: number | null }> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}/test-run`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: '{}'
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) {
			const message =
				typeof body?.error?.message === 'string'
					? body.error.message
					: typeof body?.error === 'string'
					? body.error
					: `Test run failed (${res.status})`;
			throw new Error(message);
		}
		return (body?.data ?? body) as {
			runId: number | null;
			status: string;
			errorMessage: string | null;
			conversationId: number | null;
		};
	}

	createRoutine(routine: {
		scope?: RoutineScope;
		label?: string;
		prompt?: string;
		cronExpression?: string;
		entityType?: string;
		event?: 'insert' | 'update';
		filters?: HookFilter[];
		cooldownSeconds?: number;
		agentId?: number | null;
		/** Pass 'webhook' to mint a webhook-scoped routine (server generates + registers the hash). */
		trigger?: 'webhook';
		workflowId?: number | null;
		webhookConfig?: { rateLimit?: number } | null;
	}): Promise<Routine> {
		return fetch('/api/v2/assistant/routines', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(routine)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	async updateRoutine(
		routineId: number,
		updates: {
			scope?: RoutineScope;
			label?: string;
			prompt?: string;
			cronExpression?: string | null;
			entityType?: string | null;
			event?: 'insert' | 'update' | null;
			filters?: HookFilter[] | null;
			cooldownSeconds?: number;
			enabled?: boolean;
			agentId?: number | null;
			webhookConfig?: { rateLimit?: number } | null;
		}
	): Promise<void> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.msg || `Failed to update routine (${res.status})`);
		}
	}

	deleteRoutine(routineId: number): Promise<void> {
		return fetch(`/api/v2/assistant/routines/${routineId}`, { method: 'DELETE' }).then(() => undefined);
	}

	/**
	 * WEB-7446 — rotate a webhook routine's hash. The old URL stops resolving
	 * immediately (receiver 404s unknown hashes). Returns the new hash so the
	 * caller can re-render the endpoint URL without a refetch.
	 */
	async rotateWebhookHash(routineId: number): Promise<{ webhookHash: string }> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}/rotate-webhook`, { method: 'POST' });
		const body = await res.json().catch(() => null);
		if (!res.ok) {
			throw new Error(body?.error?.msg || `Failed to rotate webhook (${res.status})`);
		}
		return body.data as { webhookHash: string };
	}

	/** WEB-7446 — recent inbound deliveries (dispatched runs) for a webhook routine. */
	getRoutineDeliveries(routineId: number): Promise<WebhookDelivery[]> {
		return fetch(`/api/v2/assistant/routines/${routineId}/deliveries`)
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	getProfile(): Promise<{ name: string; title?: string; avatarUrl: string | null; conversationCount: number }> {
		return fetch('/api/v2/assistant/profile')
			.then(res => res.json())
			.then(res => res.data);
	}

	updateProfile(updates: { name?: string; title?: string; avatarUrl?: string | null }): Promise<void> {
		return fetch('/api/v2/assistant/profile', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(() => undefined);
	}

	generateAvatar(): Promise<{ avatarUrl: string }> {
		return fetch('/api/v2/assistant/avatar/generate', { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	submitFeedback(params: {
		type: 'up' | 'down';
		reason: string;
		assistantMessage?: string;
		userMessage?: string;
		conversationId?: number;
	}): Promise<{ saved: boolean; savedValue?: string; ignored: boolean; ignoreReason?: string }> {
		return fetch('/api/v2/assistant/feedback', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeWrite(entity: string, action: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/write', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ entity, action, data })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeSendEmail(
		contactIds: number[],
		subject: string,
		body: string,
		ccContactIds?: number[],
		clientId?: number
	): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/send-email', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ contactIds, subject, body, ccContactIds, clientId })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	reset(): Promise<void> {
		return fetch('/api/v2/assistant/reset', { method: 'POST' }).then(() => undefined);
	}

	getUserUsage(): Promise<UserUsage> {
		return fetch('/api/v2/assistant/usage')
			.then(res => res.json())
			.then(res => res.data);
	}

	getAdminUsage(period?: string): Promise<AdminUsage> {
		const qs = period ? `?period=${encodeURIComponent(period)}` : '';
		return fetch(`/api/v2/assistant/admin/usage${qs}`)
			.then(res => res.json())
			.then(res => res.data);
	}

	getAccountCredits(
		period?: string,
		filters?: { userIds?: number[]; agentIds?: number[]; features?: string[] }
	): Promise<CreditsSnapshot | null> {
		const params = new URLSearchParams();
		if (period) params.set('period', period);
		if (filters?.userIds?.length) params.set('users', filters.userIds.join(','));
		if (filters?.agentIds?.length) params.set('agents', filters.agentIds.join(','));
		if (filters?.features?.length) params.set('features', filters.features.join(','));
		const qs = params.toString();
		return fetch(`/api/v2/assistant/admin/credits${qs ? `?${qs}` : ''}`)
			.then(res => res.json())
			.then(res => res.data ?? null);
	}

	getToolPricing(): Promise<PriceBookItem[]> {
		return fetch('/api/v2/assistant/admin/tool-pricing')
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	// WEB-7168 — forecast admin. Throws on non-2xx (403/429/4xx are
	// expected responses here) so the card can surface the error.
	async getForecastSettings(): Promise<ForecastSettings> {
		const res = await fetch('/api/v2/assistant/admin/forecast-settings');
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast settings request failed');
		return body.data;
	}

	async updateForecastSettings(patch: Partial<ForecastSettings>): Promise<ForecastSettings> {
		const res = await fetch('/api/v2/assistant/admin/forecast-settings', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(patch)
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast settings update failed');
		return body.data;
	}

	async getForecastCostEstimate(): Promise<ForecastCostEstimate> {
		const res = await fetch('/api/v2/assistant/admin/forecast-cost-estimate');
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throw extractApiError(body, res.status, 'Forecast cost estimate failed');
		return body.data;
	}

	listPromotionRequests(status: 'pending' | 'approved' | 'declined' = 'pending'): Promise<PromotionRequest[]> {
		return fetch(`/api/v2/assistant/admin/promotion-requests?status=${status}`)
			.then(res => res.json())
			.then(res => res.data ?? []);
	}

	requestPromotion(agentId: number, pitchText: string): Promise<PromotionRequest> {
		return fetch('/api/v2/assistant/promotion-requests', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ agentId, pitchText })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	approvePromotionRequest(id: number): Promise<{ request: PromotionRequest; newAgentId: number }> {
		return fetch(`/api/v2/assistant/admin/promotion-requests/${id}/approve`, { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	declinePromotionRequest(id: number): Promise<PromotionRequest> {
		return fetch(`/api/v2/assistant/admin/promotion-requests/${id}/decline`, { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	getAiAgentPolicies(): Promise<AiAgentPoliciesResponse> {
		return fetch('/api/v2/assistant/admin/policies')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAiAgentPolicies(updates: Partial<AiAgentPolicies>): Promise<AiAgentPoliciesResponse> {
		return fetch('/api/v2/assistant/admin/policies', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getCompanyProfile(): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveCompanyProfile(updates: Partial<CompanyProfile>): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(async res => {
			if (!res.ok) throw new Error(`saveCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	generateCompanyProfile(): Promise<CompanyProfilePayload> {
		return fetch('/api/v2/assistant/admin/company-profile/generate', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).then(async res => {
			if (!res.ok) throw new Error(`generateCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	// Idempotent first-load backfill. Kicks off a background generate+save if
	// the tenant has no profile yet; no-ops once one exists. Safe to call on
	// every workspace open.
	ensureCompanyProfile(): Promise<{ status: 'exists' | 'started' | 'generating' | 'skipped' }> {
		return fetch('/api/v2/assistant/admin/company-profile/ensure', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' }
		}).then(async res => {
			if (!res.ok) throw new Error(`ensureCompanyProfile ${res.status}`);
			const body = await res.json();
			return body.data;
		});
	}

	getAgentCreatorUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveAgentCreatorUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/agent-creator-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveAgentCreatorUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	getWorkspaceUsers(): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users')
			.then(res => res.json())
			.then(res => res.data);
	}

	saveWorkspaceUsers(userIds: number[]): Promise<{ userIds: number[] }> {
		return fetch('/api/v2/assistant/admin/workspace-users', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ userIds })
		}).then(async res => {
			if (!res.ok) throw new Error(`saveWorkspaceUsers ${res.status}`);
			const body = await res.json();
			if (body?.error) throw new Error(String(body.error?.message ?? body.error));
			return body.data;
		});
	}

	/**
	 * WEB-6650 — set or clear a single user's AI credit cap override.
	 * Pass `null` for `sek` to remove the override (user falls back to the
	 * org-wide `aiAgent.personalCapSek`).
	 */
	saveUserCreditCap(userId: number, sek: number | null): Promise<{ userId: number; sek: number | null }> {
		return fetch(`/api/v2/assistant/admin/users/${userId}/credit-cap`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ sek })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	getMyPermissions(): Promise<{ canCreateAgents: boolean; canAccessWorkspace: boolean }> {
		return fetch('/api/v2/assistant/me/permissions')
			.then(res => res.json())
			.then(res => res.data);
	}

	pageCompanyMatch(url: string): Promise<PageCompanyMatch> {
		return fetch('/api/v2/ai/page-company/match', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ url })
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyMatch failed');
			return body.data;
		});
	}

	pageCompanyExtract(params: { url: string; title: string; text: string }): Promise<PageCompanyExtract> {
		return fetch('/api/v2/ai/page-company/extract', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyExtract failed');
			return body.data;
		});
	}

	pagePersonExtract(params: { url: string; title: string; text: string }): Promise<PagePersonExtract> {
		return fetch('/api/v2/ai/page-person/extract', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pagePersonExtract failed');
			return body.data;
		});
	}

	pagePersonCompanies(name: string): Promise<PagePersonCompanyOption[]> {
		return fetch('/api/v2/ai/page-person/companies', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ name })
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pagePersonCompanies failed');
			return body.data.companies;
		});
	}

	pageCompanyAdd(params: {
		clientId?: number;
		prospectingId?: string;
		name?: string;
		domain?: string;
		orgNumber?: string;
		overrides?: PageCompanyOverrides;
		contacts: PageCompanyContact[];
	}): Promise<PageCompanyAddResult> {
		return fetch('/api/v2/ai/page-company/add', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		}).then(async res => {
			const body = await res.json();
			if (!res.ok || body.error) throw extractApiError(body, res.status, 'pageCompanyAdd failed');
			return body.data;
		});
	}
}

const resource = new Assistant();

export default resource;
